import { useEffect, useState } from 'react';
import { history } from 'umi';
import { useIntl } from 'react-intl';
import { Row, Col, Tooltip } from 'antd';
import { Button, Typography, message } from 'iglooform';
import { StarOutlined } from '@ant-design/icons';
import { AccountFilled } from 'iglooicon';
import throttle from 'lodash/throttle';
import './profile.less';
import { BackButton } from '@/components/back-button/back-button';
import { getAgencyInfo } from '../service';
import copy from 'copy-to-clipboard';
import SkeletonLoading from '@/components/skeleton-loading';
import moment from 'moment';

const Profile = () => {
  const { formatMessage } = useIntl();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<AgencyInfo>();
  const handleGetAgencyInfo = throttle(
    async () => {
      try {
        setLoading(true);
        getAgencyInfo()
          .then((res) => {
            if (res) {
              const { userInfo } = res;
              const resUserInfo = {
                name: userInfo.name,
                first_name: userInfo.first_name,
                last_name: userInfo.last_name,
                address: userInfo.address,
                agent_code: userInfo.display_id,
                city: userInfo.city,
                date_of_birth: userInfo.date_of_birth,
                email: userInfo.email,
                national_id: userInfo.national_id,
                phone_number: userInfo.phone_number,
                referral_code: userInfo.referral_code,
                agent_tier: userInfo.agent_tier,
              };
              setUserInfo(resUserInfo);
            }
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (error) {
        setLoading(false);
      }
    },
    1000,
    { leading: true, trailing: false },
  );

  useEffect(() => {
    handleGetAgencyInfo();
  }, []);

  return (
    <>
      <BackButton handleClick={() => history.goBack()} />
      {!isLoading ? (
        <Row className="profileLayout">
          <Col span={17} className="userInfo">
            <div className="userInfoTopBox">
              <div className="leftIcon">
                <AccountFilled style={{ fontSize: '55px', color: '#5858FF' }} />
              </div>
              <div className="centerContent">
                <Tooltip title={userInfo && userInfo.name}>
                  <div
                    style={{
                      width: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    <Typography level="h2" className="title">
                      {userInfo && userInfo.name}
                    </Typography>
                  </div>
                </Tooltip>
                <div className="item">
                  <Typography
                    level="body2"
                    style={{ color: '#BDBDBD', marginRight: '10px' }}
                  >
                    ID
                  </Typography>
                  <Typography level="body2">
                    {userInfo && userInfo.agent_code}
                  </Typography>
                </div>
                <div className="item">
                  <StarOutlined
                    style={{ color: '#BDBDBD', marginRight: '10px' }}
                  />
                  <Typography level="body2">
                    Tier {userInfo && userInfo.agent_tier}
                  </Typography>
                </div>
                <div className="item">
                  <StarOutlined
                    style={{ color: '#BDBDBD', marginRight: '10px' }}
                  />
                  <Typography level="body2">
                    {formatMessage({ id: 'Referral Code' })}:{' '}
                    {userInfo && userInfo.referral_code}
                  </Typography>
                  {userInfo && (
                    <a
                      style={{ marginLeft: '10px' }}
                      onClick={() => {
                        copy(userInfo.referral_code);
                        message.success(
                          formatMessage({
                            id: `Referral Code is Copied!`,
                          }),
                        );
                      }}
                    >
                      <Typography level="body2">
                        {formatMessage({ id: 'Copy' })}
                      </Typography>
                    </a>
                  )}
                </div>
              </div>
              <div className="rightButton">
                <Button onClick={() => history.push('/me/edit-profile')}>
                  <Typography level="body1">
                    {formatMessage({ id: 'Edit Profile' })}
                  </Typography>
                </Button>
              </div>
            </div>
            <div className="userInfoBottomBox">
              <div>
                <Typography level="body1" className="item">
                  {formatMessage({ id: 'First Name' })}
                </Typography>
                <Tooltip title={userInfo && userInfo.first_name}>
                  <div
                    style={{
                      display: 'inline-block',
                      width: '40%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    <Typography level="body1" style={{ whiteSpace: 'nowrap' }}>
                      {userInfo && userInfo.first_name}
                    </Typography>
                  </div>
                </Tooltip>
              </div>
              <div>
                <Typography level="body1" className="item">
                  {formatMessage({ id: 'Last Name' })}
                </Typography>
                <Tooltip title={userInfo && userInfo.last_name}>
                  <div
                    style={{
                      display: 'inline-block',
                      width: '40%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    <Typography level="body1" style={{ whiteSpace: 'nowrap' }}>
                      {userInfo && userInfo.last_name}
                    </Typography>
                  </div>
                </Tooltip>
              </div>
              <div>
                <Typography level="body1" className="item">
                  {formatMessage({ id: 'Date of Birth' })}
                </Typography>
                <Typography level="body1">
                  {userInfo &&
                    moment(userInfo.date_of_birth).format('DD / MM / YYYY')}
                </Typography>
              </div>
              <div>
                <Typography level="body1" className="item">
                  {formatMessage({ id: 'ID Number' })}
                </Typography>
                <Typography level="body1">
                  {userInfo && userInfo.national_id}
                </Typography>
              </div>
              <div>
                <Typography level="body1" className="item">
                  {formatMessage({ id: 'ID Issued Date' })}
                </Typography>
                <Typography level="body1" style={{ marginRight: '8px' }}>
                  {moment(userInfo?.id_issued_date).format('DD / MM / YYYY')}
                </Typography>
              </div>
              <div>
                <Typography level="body1" className="item">
                  {formatMessage({ id: 'Email' })}
                </Typography>
                <Typography level="body1">
                  {userInfo && userInfo.email}
                </Typography>
              </div>
              <div>
                <Typography level="body1" className="item">
                  {formatMessage({ id: 'Mobile' })}
                </Typography>
                <Typography level="body1">
                  +84 {userInfo && userInfo.phone_number}
                </Typography>
              </div>
              <div>
                <Typography level="body1" className="item">
                  {formatMessage({ id: 'Address' })}
                </Typography>
                <Typography level="body1">
                  {userInfo && userInfo.address ? userInfo.address : '/'}
                </Typography>
              </div>
              <div>
                <Typography level="body1" className="item">
                  {formatMessage({ id: 'City' })}
                </Typography>
                <Typography level="body1">
                  {userInfo && userInfo.city ? userInfo.city : '/'}
                </Typography>
              </div>
            </div>
          </Col>
        </Row>
      ) : (
        <div style={{ marginTop: '45px' }}>
          <SkeletonLoading />
        </div>
      )}
    </>
  );
};

export default Profile;
